// Difficulty tiers (rebuild.md §4.4). Every tier parameter lives here.
import type { Tier, TierParams } from '@prompts/types.ts';

export interface TierDef extends TierParams {
  label: string;
  short: string;
  description: string;
  heatPerMistake: number;
  /** What 100 heat restarts. */
  auditedRestarts: 'goal' | 'stage' | null;
  /** Highest hint rung available: 0 none, 1 nudge, 2 method, 3 worked. */
  maxHint: 0 | 1 | 2 | 3;
  /** Worked step unlocks after this many misses on an item (Infinity = never; 0 = always). */
  workedAfterMisses: number;
  extension: boolean;
  formulaSheet: 'visible' | 'one-tap' | 'hidden';
  timers: 'none' | 'soft' | 'hard';
  unlock: number;
}

export const TIER_DEFS: Readonly<Record<Tier, TierDef>> = {
  open: {
    tier: 'open',
    label: 'Tier-3 · Open',
    short: 'Open',
    description: 'Year 10, or new to the topic. Choices, guided calculators and the full hint ladder.',
    tolerance: 0.05,
    sigFigs: 'ignore',
    unitPicker: false,
    heatPerMistake: 0,
    auditedRestarts: null,
    maxHint: 3,
    workedAfterMisses: 0,
    extension: false,
    formulaSheet: 'visible',
    timers: 'none',
    unlock: 0,
  },
  rationed: {
    tier: 'rationed',
    label: 'Tier-2 · Rationed',
    short: 'Rationed',
    description: 'Units 1–4 students. Numeric entry with units; Audit Heat on.',
    tolerance: 0.02,
    sigFigs: 'advise',
    unitPicker: true,
    heatPerMistake: 10,
    auditedRestarts: 'goal',
    maxHint: 3,
    workedAfterMisses: 2,
    extension: false,
    formulaSheet: 'one-tap',
    timers: 'none',
    unlock: 0,
  },
  permit: {
    tier: 'permit',
    label: 'Tier-1 · Permit',
    short: 'Permit',
    description: 'Exam preparation. Units and significant figures required; one extension goal.',
    tolerance: null,
    sigFigs: 'enforce',
    unitPicker: true,
    heatPerMistake: 20,
    auditedRestarts: 'goal',
    maxHint: 1,
    workedAfterMisses: Infinity,
    extension: true,
    formulaSheet: 'one-tap',
    timers: 'soft',
    unlock: 0,
  },
  blackout: {
    tier: 'blackout',
    label: 'Tier-0 · Blackout',
    short: 'Blackout',
    description: 'Officially doesn’t exist. Ask Mei. No hints, hard timers, the formula sheet is hidden.',
    tolerance: null,
    sigFigs: 'enforce',
    unitPicker: true,
    heatPerMistake: 25,
    auditedRestarts: 'stage',
    maxHint: 0,
    workedAfterMisses: Infinity,
    extension: true,
    formulaSheet: 'hidden',
    timers: 'hard',
    unlock: 30,
  },
};

export const HEAT_WARNING = 70;
export const HEAT_MAX = 100;

export const tierParams = (t: Tier): TierParams => {
  const d = TIER_DEFS[t];
  return { tier: d.tier, tolerance: d.tolerance, sigFigs: d.sigFigs, unitPicker: d.unitPicker };
};

/** How many hint rungs may be revealed now on an item with `misses` wrong submissions. */
export function availableHintRungs(t: Tier, misses: number): number {
  const d = TIER_DEFS[t];
  if (d.maxHint < 3) return d.maxHint;
  return misses >= d.workedAfterMisses ? 3 : 2;
}
